import React, { useCallback, useEffect, useState } from 'react';
import Axios from '../../api/axios';
import DataGrid, { Column, FilterRow, Paging, Pager } from '../../components/BootstrapDataGrid';
import { ReportFullscreenFrame } from '../../components/ReportFullscreenFrame';
import { Button } from '../../components/BootstrapControls';
import * as glb from '../../globals';
import notify from 'devextreme/ui/notify';
import { useTabActions } from '../../contexts/tab-navigation';
import CariKartTab from '../CARI/carikart-tab.js';

const TYPE_LABELS = {
  customer_reference: 'Başkasının referansını seçme',
  representative_payment: 'Farklı müşteri temsilcisi',
  appointment_date_change: 'Randevu tarihi değişikliği',
};

const STATUS_LABELS = { pending: 'Bekliyor', approved: 'Onaylandı', rejected: 'Reddedildi' };

export default function RaporTemsilciOnay({ initialScope }) {
  const tabNavigation = useTabActions();
  const isGeneral = Number(glb.default?.KULLANICI?.KUL_YETKI) === 0
    || Number(glb.default?.KULLANICI?.KUL_YS) === 0
    || !!glb.default?.KULLANICI?.KUL_ISADMIN;
  const [scope, setScope] = useState(initialScope || (isGeneral ? 'review' : 'mine'));
  const [rows, setRows] = useState([]);
  const [notes, setNotes] = useState({});
  const [busyId, setBusyId] = useState(null);
  const openCari = useCallback((row) => {
    const carRef = Number(row?.CAR_REF || 0);
    if (!carRef || !tabNavigation?.openDetailTab) {
      notify('Cari kart açılamadı.', 'warning', 2000);
      return;
    }
    tabNavigation.openDetailTab(
      `carikart-${carRef}`,
      row?.CAR_ADSOY ? `Cari: ${row.CAR_ADSOY}` : `Cari: ${carRef}`,
      CariKartTab,
      { ops: { CAR_REF: carRef, ptype: 'update', ap: false, formReadOnly: false, inTabMode: true } }
    );
  }, [tabNavigation]);
  const load = useCallback(async () => {
    const response = await Axios.get(`${process.env.REACT_APP_BACKEND_SERVER}report/onay/liste`, { params: { scope } });
    setRows(response.data || []);
    await Axios.post(`${process.env.REACT_APP_BACKEND_SERVER}report/onay/goruldu`, { scope });
    window.dispatchEvent(new Event('approval-notifications-refresh'));
  }, [scope]);
  useEffect(() => { load().catch((err) => notify(err?.response?.data?.message || err.message, 'error', 3500)); }, [load]);

  const decide = async (id, decision) => {
    const note = String(notes[id] || '').trim();
    if (decision === 'rejected' && !note) {
      notify('Ret açıklaması zorunludur.', 'warning', 2500);
      return;
    }
    try {
      setBusyId(id);
      await Axios.post(`${process.env.REACT_APP_BACKEND_SERVER}report/onay/karar`, { id, decision, note });
      notify(decision === 'approved' ? 'Talep onaylandı.' : 'Talep reddedildi.', 'success', 2000);
      await load();
    } catch (err) {
      notify(err?.response?.data?.message || err.message, 'error', 4000);
    } finally {
      setBusyId(null);
    }
  };
  return <div style={{ padding: 12 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
      <h3 style={{ margin: 0 }}>Onay Merkezi</h3>
      <div style={{ display: 'flex', gap: 6 }}>
        {isGeneral && <Button text="Bekleyen Onaylar" type={scope === 'review' ? 'default' : 'normal'} onClick={() => setScope('review')} />}
        <Button text={isGeneral ? 'Onay Cevapları' : 'Onay Cevaplarım'} type={scope === 'mine' ? 'default' : 'normal'} onClick={() => setScope('mine')} />
        <Button text="Yenile" icon="refresh" onClick={load} />
      </div>
    </div>
    <ReportFullscreenFrame>
    <DataGrid dataSource={rows} keyExpr="id" showBorders columnAutoWidth>
      <FilterRow visible /><Paging defaultPageSize={25} /><Pager showInfo showNavigationButtons />
      <Column dataField="ONAY_TURU" caption="Onay Türü" calculateCellValue={(row) => TYPE_LABELS[row.ONAY_TURU] || row.ONAY_TURU} />
      <Column dataField="CAR_ADSOY" caption="İşlem Yapılacak Cari" cellRender={({ data }) => (
        <button
          type="button"
          onClick={() => openCari(data)}
          title="Cari kartını aç"
          style={{ padding: 0, border: 0, background: 'none', color: '#1565c0', cursor: 'pointer', textDecoration: 'underline' }}
        >
          {data.CAR_ADSOY || data.CAR_REF}
        </button>
      )} />
      <Column dataField="REFERANS_CARI" caption="Seçilen Referans" />
      <Column dataField="REFERANS_DATA_SAHIBI" caption="Referansın Data Sahibi" />
      <Column dataField="MUSTERI_TEMSILCISI" caption="Müşteri Temsilcisi" />
      <Column dataField="TALEP_EDEN" caption="Talep Eden" />
      <Column dataField="TALEP_TARIHI" caption="Talep Tarihi" dataType="datetime" />
      <Column dataField="ESKI_RANDEVU_TARIHI" caption="Eski Randevu" dataType="datetime" />
      <Column dataField="YENI_RANDEVU_TARIHI" caption="İstenen Randevu" dataType="datetime" />
      <Column dataField="DURUM" caption="Durum" calculateCellValue={(row) => STATUS_LABELS[row.DURUM] || row.DURUM} />
      <Column dataField="KARAR_VEREN" caption="Karar Veren" visible={scope === 'mine'} />
      <Column dataField="KARAR_TARIHI" caption="Karar Tarihi" dataType="datetime" visible={scope === 'mine'} />
      <Column dataField="ACIKLAMA" caption="Açıklama" cellRender={({ data }) => scope === 'review'
        ? <textarea value={notes[data.id] || ''} onChange={(event) => setNotes((prev) => ({ ...prev, [data.id]: event.target.value }))}
          placeholder="Onay/ret açıklaması" rows={2} style={{ width: 220, resize: 'vertical' }} />
        : <span>{data.ACIKLAMA || '-'}</span>} />
      <Column caption="Karar" visible={scope === 'review'} cellRender={({ data }) => <div style={{ display: 'flex', gap: 5 }}>
        <Button text="Onayla" type="success" disabled={busyId === data.id} onClick={() => decide(data.id, 'approved')} />
        <Button text="Reddet" type="danger" disabled={busyId === data.id} onClick={() => decide(data.id, 'rejected')} />
      </div>} />
    </DataGrid>
    </ReportFullscreenFrame>
  </div>;
}
